import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import IRootState from "system/types/store/IRootState";
import useConfiguration from "system/hooks/use-configuration";
import { lazy, useMemo } from "react";
import {
  DocumentsPage,
  PackagesPage,
  EndpointsByStatusPage,
  EndpointsByReasonPage,
  SubjectByReasonPage,
  EndpointRuntimeHistoryPage,
  EndpointSubjectHistory,
  EndpointPage,
  MainPage,
  PolicyEditorPage,
  PolicyVersionsPage,
  VulnerabilitiesPage,
  AskAidenDocumentsPage,
} from "components/pages";
import AskAidenAccess from "system/types/enums/AskAidenAccess";
import { Loadable } from "components/modules/ask-aiden";
import { ScrollToTop } from "components/ui";
import AdminHome from "components/admin/AdminHome";
import SubjectViewPage from "components/modules/policy-editor/components/subjectViewPage/PolicyEditorSubjectView";
import ManageAlertsPage from "components/pages/ManageAlertsPage";
import { Roles } from "system/types/enums/Roles";
import AuthResponse from "components/Intune/IntuneResponse";

const AskAidenPage = Loadable(lazy(() => import("components/pages/AskAidenPage")));
const AdvancedReporting = Loadable(lazy(() => import("components/pages/AdvancedReporting")));
const AuditReports = Loadable(lazy(() => import("components/pages/AuditReport")));
const CreateAuditReport = Loadable(lazy(() => import("components/pages/CreateAuditReport")));

const TenantRoute = ({ children }: { children: JSX.Element }) => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { defaultTenant } = useSelector((state: IRootState) => state.config);

  const isNumericTenantId = (id: string) => !isNaN(Number(id));

  // Redirect to defaultTenant if tenantId is not numeric
  if (!tenantId || !isNumericTenantId(tenantId)) {
    return <Navigate replace to={`/${defaultTenant}`} />;
  }

  return children;
};

const MainRouter = () => {
  const { defaultTenant } = useSelector((state: IRootState) => state.config);
  const { isGlobalAdmin, isFileModuleEnabled, isVulnerabilitiesModuleEnabled, isPolicyEditorEnabled, isAskAidenEnabled, isAuditReportEnabled, isWidgetReportEnabled, hasAdminPermission, roles } = useConfiguration();

  const hasPolicyEditorAccess = useMemo(
    () => isPolicyEditorEnabled && (roles.includes(Roles.PolicyAdmins) || roles.includes(Roles.PolicyViewers) || isGlobalAdmin),
    [isPolicyEditorEnabled, isGlobalAdmin, roles]
  );

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Main route */}
        <Route path="/" element={<Navigate replace to={`/${defaultTenant}`} />} />

        {/* Routes with tenantId */}
        <Route
          path="/:tenantId/*"
          element={
            <TenantRoute>
              <MainPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/endpoint/:machineId/history"
          element={
            <TenantRoute>
              <EndpointRuntimeHistoryPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/endpoint/:machineId/:time/:subject"
          element={
            <TenantRoute>
              <EndpointSubjectHistory />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/endpoint/:machineId/:time"
          element={
            <TenantRoute>
              <EndpointPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/endpoint/:machineId"
          element={
            <TenantRoute>
              <EndpointPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/endpointsbystatus/:statusString/:time"
          element={
            <TenantRoute>
              <EndpointsByStatusPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/endpointsbyreason/:statusString/:subject/:time"
          element={
            <TenantRoute>
              <EndpointsByReasonPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/endpointsbyreason/:statusString/:subject/:time/:message"
          element={
            <TenantRoute>
              <EndpointsByReasonPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/subjectbyreason/:subject"
          element={
            <TenantRoute>
              <SubjectByReasonPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/packages"
          element={
            <TenantRoute>
              <PackagesPage />
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/vulnerabilities"
          element={<TenantRoute>{isVulnerabilitiesModuleEnabled ? <VulnerabilitiesPage /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>}
        />
        <Route
          path="/:tenantId/documents"
          element={<TenantRoute>{isFileModuleEnabled ? <DocumentsPage /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>}
        />
        <Route
          path="/:tenantId/askAiden"
          element={
            <TenantRoute>
              {isAskAidenEnabled !== AskAidenAccess.NoAccess ? <AskAidenPage /> : <Navigate replace to={`/${defaultTenant}`} />}
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/askaidentdocuments"
          element={
            <TenantRoute>
              {isAskAidenEnabled !== AskAidenAccess.NoAccess && hasAdminPermission ? (
                <AskAidenDocumentsPage />
              ) : (
                <Navigate replace to={`/${defaultTenant}`} />
              )}
            </TenantRoute>
          }
        />
        <Route
          path="/:tenantId/policyeditor"
          element={
            <TenantRoute>{hasPolicyEditorAccess ? <PolicyVersionsPage /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>
          }
        />
        <Route
          path="/:tenantId/policyeditor/edit/:versionId"
          element={
            <TenantRoute>{hasPolicyEditorAccess ? <PolicyEditorPage /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>
          }
        />
        <Route
          path="/:tenantId/policyeditorsubject"
          element={<TenantRoute>{hasPolicyEditorAccess ? <SubjectViewPage /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>}
        />
        <Route
          path="/:tenantId/admin"
          element={<TenantRoute>{hasAdminPermission ? <AdminHome /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>}
        />
        <Route
          path="/:tenantId/managealert"
          element={<TenantRoute>{isGlobalAdmin ? <ManageAlertsPage /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>}
        />
        <Route
          path="/:tenantId/advancedreporting"
          element={<TenantRoute>{isWidgetReportEnabled ? <AdvancedReporting /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>}
        />

        <Route path="/auth-response" element={<AuthResponse />} />

        <Route
          path="/:tenantId/auditreport"
          element={<TenantRoute>{isAuditReportEnabled ? <AuditReports /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>}
        />
        <Route
          path="/:tenantId/auditreport/create"
          element={<TenantRoute>{isAuditReportEnabled ? <CreateAuditReport /> : <Navigate replace to={`/${defaultTenant}`} />}</TenantRoute>}
        />
        {/* Catch-all route for any other URL */}
        <Route path="*" element={<Navigate replace to={`/${defaultTenant}`} />} />
      </Routes>
    </>
  );
};

export default MainRouter;